import React, { useEffect, useState } from "react";
import { KpiCard } from "../shared/KpiCard";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { EmptyState } from "../shared/EmptyState";
import { ErrorState } from "../shared/ErrorState";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatCurrency, formatNumber } from "../../lib/utils";
import { useTaxConfig, computeGst } from "../../lib/tax";
import { useListState } from "../../hooks/useListState";
import { Wallet, CheckCircle2, XCircle } from "lucide-react";

export function FinancialTab() {
  const taxCfg = useTaxConfig();
  const [dateFrom, setDateFrom] = useListState("reports.financial.dateFrom", "");
  const [dateTo, setDateTo] = useListState("reports.financial.dateTo", "");
  const [planId, setPlanId] = useListState("reports.financial.planId", "");
  const [plans, setPlans] = useState([]);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [planWise, setPlanWise] = useState({ loading: true, rows: [] });

  useEffect(() => {
    apiClient.get("/plans/active").then(({ data }) => setPlans(data)).catch(() => {});
    apiClient.get("/reports/plan-wise-totals")
      .then(({ data }) => setPlanWise({ loading: false, rows: data }))
      .catch(() => setPlanWise({ loading: false, rows: [] }));
  }, []);

  const load = () => {
    setState({ loading: true, error: null, data: null });
    apiClient.get("/reports/financial", { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined, plan_id: planId || undefined } })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load the financial report."), data: null }));
  };

  useEffect(load, [dateFrom, dateTo, planId]);

  const r = state.data;

  return (
    <div className="mt-4 space-y-4" data-testid="financial-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="financial-date-from">From</Label>
          <Input id="financial-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="financial-date-from-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="financial-date-to">To</Label>
          <Input id="financial-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="financial-date-to-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="financial-plan">Plan</Label>
          <Select value={planId || "all"} onValueChange={(v) => setPlanId(v === "all" ? "" : v)}>
            <SelectTrigger id="financial-plan" className="w-48" data-testid="financial-plan-select"><SelectValue placeholder="All plans" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              {plans.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(dateFrom || dateTo || planId) && (
          <Button variant="secondary" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setPlanId(""); }} data-testid="financial-clear-button">Clear</Button>
        )}
      </div>

      {state.error ? (
        <ErrorState message={state.error} onRetry={load} testId="financial-error" />
      ) : state.loading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard testId="financial-kpi-approved" title="Approved" icon={CheckCircle2} accent value={formatCurrency(r.total_approved_amount)} hint={`${formatNumber(r.count_approved)} payment(s) · incl. GST ${formatCurrency(computeGst(r.total_approved_amount, taxCfg).total)}`} />
          <KpiCard testId="financial-kpi-pending" title="Pending" icon={Wallet} value={formatCurrency(r.total_pending_amount)} hint={`${formatNumber(r.count_pending)} payment(s) · incl. GST ${formatCurrency(computeGst(r.total_pending_amount, taxCfg).total)}`} />
          <KpiCard testId="financial-kpi-declined" title="Declined" icon={XCircle} value={formatCurrency(r.total_declined_amount)} hint={`${formatNumber(r.count_declined)} payment(s)`} />
        </div>
      )}

      {r && r.reconciliation_mismatches > 0 && (
        <Card data-testid="financial-mismatch-card">
          <CardContent className="p-4">
            <p className="mb-2 font-heading text-sm font-semibold text-danger">
              {r.reconciliation_mismatches} price mismatch(es) found (of {r.reconciliation_checked} checked)
            </p>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Subscription</TableHead><TableHead>Sub. Price</TableHead><TableHead>Payment</TableHead><TableHead>Paid Amount</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {r.mismatch_samples.map((m) => (
                  <TableRow key={m.payment_id} data-testid={`financial-mismatch-row-${m.payment_id}`}>
                    <TableCell>#{m.subscription_id}</TableCell>
                    <TableCell>{m.subscription_price}</TableCell>
                    <TableCell>#{m.payment_id}</TableCell>
                    <TableCell>{m.payment_amout}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card data-testid="financial-plan-wise-card">
        <CardContent className="p-0">
          <p className="p-4 pb-0 font-heading text-sm font-semibold text-text-main">Revenue by Plan (all time, approved only)</p>
          {planWise.loading ? <Skeleton className="m-4 h-32" /> : planWise.rows.length === 0 ? (
            <EmptyState title="No plan revenue yet" testId="financial-plan-wise-empty" />
          ) : (
            <Table data-testid="financial-plan-wise-table">
              <TableHeader>
                <TableRow><TableHead>Plan</TableHead><TableHead>Active Subscriptions</TableHead><TableHead>Approved Revenue</TableHead><TableHead>GST</TableHead><TableHead>Total (incl. GST)</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {planWise.rows.map((p) => {
                  const bd = computeGst(p.approved_revenue, taxCfg);
                  return (
                  <TableRow key={p.plan_id} data-testid={`financial-plan-wise-row-${p.plan_id}`}>
                    <TableCell className="font-medium">{p.plan_name}</TableCell>
                    <TableCell>{formatNumber(p.active_subscriptions)}</TableCell>
                    <TableCell className="text-text-muted">{formatCurrency(p.approved_revenue)}</TableCell>
                    <TableCell className="text-text-muted">{formatCurrency(bd.gst)}</TableCell>
                    <TableCell className="font-semibold text-primary">{formatCurrency(bd.total)}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
